import React from 'react';
import { useLocation } from 'react-router-dom';
import { ErrorBoundary, ErrorBoundaryProps } from './ErrorBoundary';

/**
 * RouteErrorBoundary Component
 *
 * A route-aware error boundary that automatically resets its error state
 * when the user navigates to a different route.
 *
 * This solves the problem where an error occurs on one page, and pressing
 * the browser back button still shows the error until page refresh.
 *
 * Implementation: Uses React's `key` prop to force a remount of the
 * ErrorBoundary when the location changes, which resets its internal state.
 *
 * @example
 * ```tsx
 * // In a layout component
 * <RouteErrorBoundary fallback={ErrorFallback}>
 *   <Outlet />
 * </RouteErrorBoundary>
 * ```
 */
export const RouteErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  children,
  ...props
}): React.ReactElement => {
  const location = useLocation();

  // Use the PATHNAME ALONE as the key: navigation to another page resets the boundary, while a
  // search-param update does not. Keying on search remounted the whole page subtree on every
  // query-string write — which silently destroyed page-local state each time a page touched its
  // own params, and broke the 4G compose deep link (?compose=… seeds a modal and then clears
  // itself with replace:true; the clear remounted the page and closed the modal it had just
  // opened — sprint-251 Goal-4 walk finding, invisible to unit tests which render no boundary).
  const locationKey = location.pathname;

  return (
    <ErrorBoundary key={locationKey} {...props}>
      {children}
    </ErrorBoundary>
  );
};
